import {
  X,
  Smartphone,
  Glasses,
  Watch,
  Tablet,
  Monitor,
  Sparkles,
  RadioTower,
  Wifi,
  ShieldCheck,
  ChevronRight,
  Bluetooth,
  Settings2,
} from "lucide-react";
import { useState } from "react";

const AVATAR_URL =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=facearea&facepad=2.4&w=320&h=320&q=80";

const DEVICES = [
  { key: "iphone",      Icon: Smartphone, name: "Dr. Rao's iPhone",       model: "iPhone 17 Pro",         detail: "Handoff ready · Nearby",  battery: 82,   status: "connected" },
  { key: "glasses",     Icon: Glasses,    name: "Even G2 Glasses",         model: "Loupe overlay",         detail: "Standby · 4.5 m",         battery: 47,   status: "connected" },
  { key: "watch",       Icon: Watch,      name: "Dr. Rao's Apple Watch",   model: "Series 10",             detail: "Haptic alerts on",        battery: 64,   status: "connected" },
  { key: "ipad",        Icon: Tablet,     name: "OR Imaging iPad",         model: "iPad Pro 13\"",         detail: "Idle · scrub bay",        battery: 91,   status: "idle" },
  { key: "workstation", Icon: Monitor,    name: "OR Workstation",          model: "Studio Display · 27\"", detail: "Wired · OR-4",            battery: null, status: "active" },
];

const STATUS_META = {
  connected: { label: "Connected", tone: "hsl(var(--nn-blue))" },
  active:    { label: "Active",    tone: "hsl(var(--nn-ok))" },
  idle:      { label: "Idle",      tone: "rgba(255,255,255,0.4)" },
};

export default function InspectorSidebar({ onClose }) {
  const [selected, setSelected] = useState("iphone");
  const device = DEVICES.find((d) => d.key === selected);

  return (
    <aside
      className="nn-glass-sidebar flex h-full w-full flex-col overflow-hidden"
      role="complementary"
      aria-label="Workspace inspector"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <Settings2 className="h-3.5 w-3.5 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.6} />
          <span className="nn-eyebrow" style={{ letterSpacing: "0.18em" }}>
            Workspace
          </span>
        </div>
        <button
          className="nn-icon-btn"
          aria-label="Close"
          onClick={onClose}
          style={{ height: 26, width: 26 }}
        >
          <X className="h-3 w-3" strokeWidth={1.8} />
        </button>
      </div>

      <div className="nn-scroll flex-1 overflow-y-auto px-4 pb-4">
        {/* 1. Profile card */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3.5">
          <div className="flex items-start gap-3">
            <div className="relative">
              <img
                src={AVATAR_URL}
                alt="Dr. Maya Rao"
                className="h-14 w-14 rounded-full object-cover"
                style={{
                  boxShadow:
                    "inset 0 0 0 1px rgba(255,255,255,0.14), 0 0 0 2px rgba(0,0,0,0.4)",
                }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span className="absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full bg-[hsl(var(--nn-ok))] ring-2 ring-[#0e1524]" />
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[14.5px] font-semibold tracking-tight text-white">
                Dr. Maya Rao
              </div>
              <div className="mt-0.5 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                Attending · Neurosurgery
              </div>
              <div className="text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                University Medical Center
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <span className="nn-chip nn-chip-blue">Skull Base</span>
            <span className="nn-chip">Neuro-Oncology</span>
            <span className="nn-chip">Cerebrovascular</span>
          </div>
        </div>

        {/* 2. QR pairing */}
        <div
          className="mt-3 rounded-2xl border border-white/[0.08] p-3.5"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
          }}
        >
          <div className="flex items-center gap-3">
            <DeviceIllustration device={device} />
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[12.5px] font-medium text-white">
                {device.name}
              </div>
              <div className="mt-0.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
                {device.model}
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
                <Bluetooth className="h-3 w-3 text-[hsl(var(--nn-blue))]" strokeWidth={1.8} />
                Scan to connect
              </div>
            </div>
            <QRCode />
          </div>
          <div className="mt-2.5 flex items-center justify-between border-t border-white/[0.06] pt-2.5">
            <span className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {DEVICES.length} devices paired
            </span>
            <button className="text-[10.5px] font-medium text-[hsl(var(--nn-blue))] hover:text-[hsl(var(--nn-blue))]/80">
              Add device →
            </button>
          </div>
        </div>

        {/* 3. Connected devices */}
        <div className="mt-4 mb-2 flex items-center justify-between">
          <div className="nn-eyebrow">Connected devices</div>
          <span className="text-[10px] font-mono text-[hsl(var(--nn-ink-faint))]">
            {DEVICES.length}
          </span>
        </div>
        <div className="overflow-hidden rounded-xl border border-white/[0.07]">
          {DEVICES.map((d, i) => (
            <DeviceRow
              key={d.key}
              device={d}
              selected={selected === d.key}
              onSelect={() => setSelected(d.key)}
              divider={i < DEVICES.length - 1}
            />
          ))}
        </div>

        {/* 4. Workspace status */}
        <div className="mt-4 mb-2 nn-eyebrow">Workspace status</div>
        <div className="grid grid-cols-3 gap-2">
          <StatusCard Icon={RadioTower}  label="Memory sync" value="2m ago"  tone="ok"    />
          <StatusCard Icon={Wifi}        label="Latency"     value="42 ms"   tone="blue"  />
          <StatusCard Icon={ShieldCheck} label="Provenance"  value="Enabled" tone="amber" />
        </div>

        {/* 5. Footer actions */}
        <div className="mt-4 flex items-center gap-2">
          <button className="nn-btn nn-btn-primary flex-1 justify-center">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            Hand off session
          </button>
          <button className="nn-btn nn-btn-ghost text-[hsl(var(--nn-ink-faint))]">
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

function DeviceRow({ device, selected, onSelect, divider }) {
  const s = STATUS_META[device.status];
  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" ? onSelect() : null)}
      className="group cursor-pointer transition-colors duration-200"
      style={{
        background: selected ? "rgba(140,180,250,0.10)" : "transparent",
        borderBottom: divider ? "1px solid rgba(255,255,255,0.05)" : "none",
      }}
    >
      <div className="flex items-center gap-3 px-3 py-2.5 transition-colors duration-200 hover:bg-white/[0.04]">
        <div
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/[0.09] bg-white/[0.04]"
          style={{ color: selected ? "hsl(var(--nn-blue))" : "rgba(255,255,255,0.75)" }}
        >
          <device.Icon className="h-3.5 w-3.5" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[12px] font-medium text-white">
              {device.name}
            </span>
            <span
              className="h-1 w-1 shrink-0 rounded-full"
              style={{ background: s.tone }}
            />
          </div>
          <div className="truncate text-[10px] text-[hsl(var(--nn-ink-muted))]">
            {device.detail}
          </div>
        </div>
        {device.battery !== null && <Battery pct={device.battery} />}
        <ChevronRight
          className="h-3.5 w-3.5 text-[hsl(var(--nn-ink-faint))] transition-transform duration-200 group-hover:translate-x-0.5"
          strokeWidth={1.8}
        />
      </div>
      {selected && (
        <div
          className="px-3 pb-3 pt-1"
          style={{ animation: "nn-fade-in 220ms cubic-bezier(0.2,0.7,0.2,1) both" }}
        >
          <div className="flex flex-wrap gap-1.5">
            <span className="nn-chip nn-chip-blue">
              {STATUS_META[device.status].label}
            </span>
            {device.battery !== null && (
              <span className="nn-chip">Battery {device.battery}%</span>
            )}
            <span className="nn-chip">{device.model}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <button className="nn-btn nn-btn-ghost !py-1.5 !px-2.5 !text-[11px]">
              Rename
            </button>
            <button className="nn-btn nn-btn-ghost !py-1.5 !px-2.5 !text-[11px]">
              Forget
            </button>
            <button className="nn-btn !py-1.5 !px-2.5 !text-[11px]">
              Show details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Battery({ pct }) {
  const color =
    pct >= 60 ? "hsl(var(--nn-ok))" : pct >= 25 ? "hsl(var(--nn-warn))" : "hsl(var(--nn-danger))";
  return (
    <div className="flex items-center gap-1">
      <div className="relative h-2.5 w-6 rounded-[3px] border border-white/25">
        <div
          className="absolute inset-y-0 left-0 rounded-[2px]"
          style={{ width: `${pct}%`, background: color, opacity: 0.95 }}
        />
      </div>
      <span className="text-[9.5px] font-mono text-[hsl(var(--nn-ink-muted))]">
        {pct}%
      </span>
    </div>
  );
}

const TONE = {
  ok:    "hsl(var(--nn-ok))",
  blue:  "hsl(var(--nn-blue))",
  amber: "hsl(var(--nn-amber))",
};
function StatusCard({ Icon, label, value, tone }) {
  return (
    <div
      className="flex flex-col gap-1 rounded-xl p-2.5"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.09)",
      }}
    >
      <div className="flex items-center gap-1.5" style={{ color: TONE[tone] }}>
        <Icon className="h-3 w-3" strokeWidth={1.8} />
        <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--nn-ink-faint))]">
          {label}
        </span>
      </div>
      <div className="text-[12px] font-medium text-white">{value}</div>
    </div>
  );
}

function DeviceIllustration({ device }) {
  const D = device.Icon;
  return (
    <div
      className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl"
      style={{
        background:
          "linear-gradient(160deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.14)",
        color: "hsl(var(--nn-blue))",
      }}
    >
      <D className="h-6 w-6" strokeWidth={1.4} />
    </div>
  );
}

function QRCode() {
  const size = 9;
  const cells = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const seed = (x * 13 + y * 7 + 3) % 5;
      const isCorner =
        (x < 3 && y < 3) || (x >= size - 3 && y < 3) || (x < 3 && y >= size - 3);
      const filled = isCorner
        ? (x === 0 || x === 2 || y === 0 || y === 2 || (x === 1 && y === 1))
        : seed < 2;
      if (filled) cells.push([x, y]);
    }
  }
  return (
    <div
      className="grid h-16 w-16 shrink-0 place-items-center rounded-xl"
      style={{
        background: "rgba(255,255,255,0.94)",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
      }}
    >
      <svg viewBox={`0 0 ${size} ${size}`} width="52" height="52" shapeRendering="crispEdges">
        {cells.map(([x, y]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#0e1424" />
        ))}
      </svg>
    </div>
  );
}
